import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPost, operationsClient } from '@/lib/api/client';
import { taxZoneEndpoints } from '@/lib/api/endpoints';
import { taxZoneKeys, taxGroupKeys } from '@/lib/queryKeys';

export interface ManualZoneTaxInput {
  name: string;
  /** Decimal fraction — 0.0625 is 6.25%. */
  rate: number;
  appliesTo: 'all' | 'labor' | 'materials' | 'freight';
  jurisdictionType?: 'state' | 'county' | 'city' | 'district';
}

export interface CreateManualTaxZoneRequest {
  name: string;
  /** Omit for a manual-apply-only zone (no automatic geography). */
  zipCodes?: string[];
  stateCode: string;
  city?: string;
  taxes: ManualZoneTaxInput[];
  replaceExisting?: boolean;
}

export interface CreateManualTaxZoneResponse {
  taxGroupId: string;
  name: string;
  combinedRate: number;
  taxCodeIds: string[];
  zipCodes: string[];
  reassignedZipCodes: string[];
}

/**
 * POST /tax-zones/manual — the operator-typed zone flow: the operator
 * types every rate; no tax data provider involved. One call
 * creates the codes, the group, and the per-ZIP zone rows, immediately
 * resolvable.
 */
export function useCreateManualTaxZone() {
  const qc = useQueryClient();
  return useApiMutation<CreateManualTaxZoneResponse, CreateManualTaxZoneRequest>({
    mutationFn: (body) =>
      apiPost<CreateManualTaxZoneResponse>(operationsClient, taxZoneEndpoints.createManual(), body),
    onSuccess: (zone) => {
      toast.success(
        zone.zipCodes.length > 0
          ? `Tax zone "${zone.name}" created — ${zone.zipCodes.length} ZIP${
              zone.zipCodes.length === 1 ? '' : 's'
            } at ${(zone.combinedRate * 100).toFixed(2)}% combined.`
          : `Tax zone "${zone.name}" created at ${(zone.combinedRate * 100).toFixed(2)}% — no automatic geography; apply it via a pin or a rule.`,
      );
      if (zone.reassignedZipCodes.length > 0) {
        toast.warning(
          `Took over ${zone.reassignedZipCodes.length} ZIP${
            zone.reassignedZipCodes.length === 1 ? '' : 's'
          } from other zones: ${zone.reassignedZipCodes.join(', ')}`,
        );
      }
      void qc.invalidateQueries({ queryKey: taxZoneKeys.all() });
      void qc.invalidateQueries({ queryKey: taxGroupKeys.all() });
    },
  });
}
